package com.example.positionahead.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.positionahead.model.*
import com.example.positionahead.ui.theme.gainColor
import com.example.positionahead.ui.theme.lossColor
import com.example.positionahead.util.formatCurrency
import com.example.positionahead.viewmodel.PortfolioUiState

@Composable
fun SimulationScreen(
    uiState: PortfolioUiState,
    onUpdateCapital: (Double) -> Unit,
    onNavigateToPlan: () -> Unit,
    modifier: Modifier = Modifier
) {
    var capitalInput by remember { mutableStateOf(uiState.availableCapital.toString()) }

    LazyColumn(
        modifier = modifier.fillMaxSize().background(MaterialTheme.colorScheme.background),
        contentPadding = PaddingValues(bottom = 32.dp),
        verticalArrangement = Arrangement.spacedBy(20.dp)
    ) {
        item {
            CapitalInputHeader(
                capitalValue = capitalInput,
                onValueChange = { 
                    capitalInput = it
                    it.toDoubleOrNull()?.let(onUpdateCapital)
                }
            )
        }

        item {
            PaddingWrapper {
                Text("What would you do with my next $${capitalInput}?", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Black)
            }
        }

        items(uiState.capitalScenarios) { scenario ->
            PaddingWrapper {
                ScenarioCard(scenario)
            }
        }

        item {
            PaddingWrapper {
                HorizontalDivider(modifier = Modifier.padding(vertical = 12.dp))
                StrategyComparisonPrompt(onNavigateToPlan)
            }
        }
    }
}

@Composable
private fun StrategyComparisonPrompt(onClick: () -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.secondary),
        shape = RoundedCornerShape(20.dp)
    ) {
        Column(modifier = Modifier.padding(20.dp)) {
            Text("Open Plan comparison", color = Color.White, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Black)
            Text("Side-by-side strategies, trades, and save — same tools as the Plan tab.", color = Color.White.copy(alpha = 0.7f), style = MaterialTheme.typography.bodySmall)
        }
    }
}

@Composable
private fun CapitalInputHeader(
    capitalValue: String,
    onValueChange: (String) -> Unit
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(MaterialTheme.colorScheme.primary)
            .padding(vertical = 40.dp, horizontal = 24.dp),
        contentAlignment = Alignment.Center
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text("$", style = MaterialTheme.typography.displayMedium, color = Color.White)
                androidx.compose.foundation.text.BasicTextField(
                    value = capitalValue,
                    onValueChange = onValueChange,
                    textStyle = MaterialTheme.typography.displayLarge.copy(
                        fontWeight = FontWeight.Black,
                        color = Color.White,
                        fontSize = 72.sp
                    ),
                    modifier = Modifier.width(IntrinsicSize.Min)
                )
            }
            Text(
                text = "Available Capital",
                color = Color.White.copy(alpha = 0.8f),
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.Bold
            )
        }
    }
}

@Composable
private fun ScenarioCard(scenario: CapitalScenario) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        shape = RoundedCornerShape(20.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.padding(20.dp)) {
            Text(scenario.title, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Black)
            Spacer(modifier = Modifier.height(4.dp))
            Text(scenario.description, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            
            Spacer(modifier = Modifier.height(16.dp))
            
            scenario.allocations.forEach { (symbol, amount) ->
                Row(
                    modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(symbol, fontWeight = FontWeight.Bold)
                    Text(formatCurrency(amount), color = MaterialTheme.colorScheme.primary, fontWeight = FontWeight.Black)
                }
            }
        }
    }
}

@Composable
private fun PaddingWrapper(content: @Composable () -> Unit) {
    Column(modifier = Modifier.padding(horizontal = 20.dp)) {
        content()
    }
}
